import { Avatar } from "@/components/ui/avatar";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { FieldActions } from "@/components/actions";
import { FieldDescriptionTrigger, FieldLabelWithDescription } from "@/components/layout-2/field-label-with-description";
import { cn, formatCurrencyValue, formatDateValue, stringToColor } from "@/lib/utils";
import { ApprenticeshipRegistrationStatus } from "@/types/apprenticeship/apprentice";
import { FieldDataType } from "@/types/dynamic-detail";
import { Mail, Phone, Smartphone } from "lucide-react";
import React from "react";
import { Link, NavLink } from "react-router-dom";
import { formatFieldValue } from "./dynamic-detail-formatters";
import { get_apprenticeship_status_badge, timeAgo } from "./field-rendering-functions";
import { SimpleMarkdownRenderer } from "@/components/ui";
import { formatDurationHHMMSS, formatHumanDuration, formatPercentageFromRatio } from "./format-time";

export interface FieldRendererOptions {
    // Style options
    layout?: "grid" | "flex";
    showLabel?: boolean;
    labelClassName?: string;
    valueClassName?: string;
    containerClassName?: string;
    isHeaderField?: boolean;
    // Behavior options
    truncate?: boolean;
    showUnit?: boolean;
    showIcon?: boolean;
    descriptionTrigger?: FieldDescriptionTrigger;

    // Grid specific options
    largeField?: boolean;
    gridCols?: number;

    // Flex specific options
    flexDirection?: "row" | "col";
    flexGap?: number;

    // Custom options
    disableFieldActions?: boolean;
}

/** A field value with nothing to show: what `hide_if_empty` hides and `expected` flags. */
export function isEmptyFieldValue(value: unknown): boolean {
    return !value && value !== 0 && value !== false;
}

/** Fields the record should carry (`Field.expected`) and does not, but those the system fills later (`pending_label`). */
export function missingExpectedFields(fields: FieldDataType[] | null | undefined): FieldDataType[] {
    return (fields ?? []).filter(field => field.expected && !field.pending_label && isEmptyFieldValue(field.value));
}

export function renderField(field: FieldDataType, options: FieldRendererOptions = {}): React.ReactNode {
    const { layout = "grid", showLabel = true, labelClassName = "text-xs text-gray-600", valueClassName = "text-sm", containerClassName = "", largeField = false, truncate = true } = options;

    let { label, value } = field;
    const description = field.description;

    // If no value, return a placeholder
    if (isEmptyFieldValue(value)) {
        // A value the record should carry (`Field.expected`): shown and flagged, never hidden.
        if (field.expected && field.pending_label) {
            const pending = <span className={cn("text-muted-foreground italic", valueClassName)}>{field.pending_label}</span>;
            return renderLayout(pending, { label, description, showLabel, labelClassName, layout, containerClassName, largeField, truncate, descriptionTrigger: options.descriptionTrigger });
        }
        if (field.expected) {
            const missing = <span className={cn("rounded bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-800", valueClassName && "text-xs")}>À compléter</span>;
            return renderLayout(missing, { label, description, showLabel, labelClassName, layout, containerClassName, largeField, truncate, descriptionTrigger: options.descriptionTrigger });
        }
        if (field.hide_if_empty) {
            return null;
        }
        return renderEmptyField(label, showLabel, labelClassName, valueClassName, layout, containerClassName, truncate, description, options.descriptionTrigger);
    }
    // Render specific content based on field type
    const content = renderFieldContent(field, options, layout);

    // Render generic layout
    return renderLayout(content, { label, description, showLabel, labelClassName, layout, containerClassName, largeField, truncate, descriptionTrigger: options.descriptionTrigger });
}

/**
 * Render content specific to field type
 */
function renderFieldContent(field: FieldDataType, options: FieldRendererOptions, layout: "grid" | "flex"): React.ReactNode {
    const { field_type } = field;
    const { showUnit = true, largeField = false, valueClassName, truncate = true } = options;

    switch (field_type) {
        case "user":
            return renderUserContent(field, options.isHeaderField || false);

        case "email":
            return renderEmailContent(field, options.isHeaderField || false, options.disableFieldActions || false);

        // `tel` is the HTML input alias some forms declare (see `dynamic-form.ts`); it is a phone
        // number on display too, so it gets the same icon and click-to-call as `phone`.
        case "tel":
        case "phone":
            return renderPhoneContent(field, options.isHeaderField || false, options.disableFieldActions || false);

        case "boolean":
        case "checkbox":
            return renderBooleanContent(field, options.isHeaderField || false);

        case "datetime":
            return renderDateTimeContent(field, options.isHeaderField || false);

        case "date":
            return renderDateContent(field, options.isHeaderField || false);

        case "time":
            return renderTimeContent(field, options.isHeaderField || false);

        case "duration":
            return renderDurationContent(field, options.isHeaderField || false);

        case "percentage":
            return renderPercentageContent(field, options.isHeaderField || false);

        case "currency":
            return renderCurrencyContent(field, options.isHeaderField || false);

        case "url":
            return renderUrlContent(field, options.isHeaderField || false);

        case "age":
            return renderAgeContent(field, options.isHeaderField || false);

        case "since":
            return renderSinceContent(field, options.isHeaderField || false);

        case "city":
            return renderCityContent(field, options.isHeaderField || false);

        case "last_follow_up":
            return renderSinceContent(field, options.isHeaderField || false);

        case "badge":
            return renderBadgeContent(field);

        case "registration_status":
            return renderRegistrationStatusContent(field, options.isHeaderField || false);

        case "apprenticeship_type":
            return renderApprenticeshipTypeContent(field, options.isHeaderField || false);

        case "json":
            return renderJsonContent(field);

        case "markdown":
            return renderMarkdownContent(field, { showUnit, largeField, valueClassName, layout, isHeaderField: options.isHeaderField || false, truncate });

        case "alert":
        case "alert-info":
        case "alert-warning":
        case "alert-error":
            return renderAlert(field, { showUnit, largeField, valueClassName, layout, isHeaderField: options.isHeaderField || false, truncate });

        default:
            return renderDefaultContent(field, { showUnit, largeField, valueClassName, layout, isHeaderField: options.isHeaderField || false, truncate });
    }
}

/**
 * Render generic layout (grid or flex)
 */
function renderLayout(
    content: React.ReactNode,
    options: {
        label: string;
        description?: string | null;
        showLabel: boolean;
        labelClassName: string;
        layout: "grid" | "flex";
        containerClassName: string;
        largeField?: boolean;
        isHeaderField?: boolean;
        truncate?: boolean;
        descriptionTrigger?: FieldDescriptionTrigger;
    }
): React.ReactNode {
    const { label, description, showLabel, labelClassName, layout, containerClassName, largeField, isHeaderField, truncate = true, descriptionTrigger } = options;

    if (layout === "flex") {
        return (
            <div className={cn("flex w-full min-w-0 flex-col items-start", containerClassName)}>
                {showLabel && (
                    <FieldLabelWithDescription label={label} description={description} descriptionTrigger={descriptionTrigger} as="p" className={cn(truncate && "truncate", labelClassName)} />
                )}
                <div className={cn("flex min-w-0 items-center", isHeaderField && "max-w-40")}>{content}</div>
            </div>
        );
    }

    // Grid layout
    return (
        <div className={cn("grid items-start border-b pb-1", containerClassName)}>
            {showLabel && (
                <FieldLabelWithDescription
                    label={label}
                    description={description}
                    descriptionTrigger={descriptionTrigger}
                    className={cn("col-span-2 mt-0.5 min-w-0", truncate && "truncate text-ellipsis", "pr-1", labelClassName)}
                />
            )}
            <div className={cn("flex min-w-0 items-center text-sm", largeField ? "col-span-7" : "col-span-3", isHeaderField && "max-w-40")}>{content}</div>
        </div>
    );
}

// Content rendering functions by type
function renderUserContent(field: FieldDataType, isHeaderField: boolean): React.ReactNode {
    let { value, href } = field;

    if (value && typeof value === "object") {
        value = value.value ?? value.raw_value;
    }

    const avatarContent = (
        <Avatar className={cn("flex h-6 w-6 items-center justify-center text-[10px] font-medium text-white")} style={{ backgroundColor: stringToColor(value) }}>
            {value
                .split(" ")
                .map((name: string) => name[0])
                .join("")
                .slice(0, 2)}
        </Avatar>
    );

    const valueContent = href ? (
        <NavLink to={href} className="truncate text-base hover:text-blue-500" title={value}>
            <span className={cn("overflow-hidden truncate text-ellipsis whitespace-nowrap", isHeaderField ? "max-w-40" : "text-sm")}>{value}</span>
        </NavLink>
    ) : (
        <span className={cn("overflow-hidden truncate text-ellipsis whitespace-nowrap", isHeaderField ? "max-w-40" : "text-sm")} title={value}>
            {value}
        </span>
    );

    return (
        <div className="flex items-center gap-1">
            {avatarContent}
            {valueContent}
        </div>
    );
}

function renderEmailContent(field: FieldDataType, isHeaderField: boolean, disableFieldActions: boolean): React.ReactNode {
    let { value } = field;

    if (value && typeof value === "object") {
        value = value.value ?? value.raw_value;
    }

    if (disableFieldActions) {
        return (
            <span className={cn("flex-1 overflow-hidden truncate text-ellipsis whitespace-nowrap", isHeaderField && "max-w-40")} title={value}>
                {value}
            </span>
        );
    }

    return (
        <FieldActions
            email={value}
            className="w-full"
            children={
                <span className="flex w-full min-w-0 items-center overflow-hidden" title={value}>
                    <Mail className="mr-1.5 inline-block flex-shrink-0" size={15} />
                    <span className={cn("flex-1 overflow-hidden truncate text-ellipsis whitespace-nowrap", isHeaderField && "max-w-40")}>{value}</span>
                </span>
            }
        />
    );
}

function renderPhoneContent(field: FieldDataType, isHeaderField: boolean, disableFieldActions: boolean): React.ReactNode {
    let { value, field_name } = field;

    if (value && typeof value === "object") {
        value = value.value ?? value.raw_value;
    }

    const isMobile = field_name.includes("mobile");
    const PhoneIconComponent = isMobile ? Smartphone : Phone;

    if (disableFieldActions) {
        return (
            <span className={cn("flex-1 overflow-hidden truncate text-ellipsis whitespace-nowrap", isHeaderField && "max-w-40")} title={value}>
                {value}
            </span>
        );
    }
    return (
        <FieldActions
            phone_number={value}
            children={
                <span className="flex min-w-0 items-center" title={value}>
                    <PhoneIconComponent className="mr-1.5 inline-block flex-shrink-0" size={15} />
                    <span className={cn("flex-1 overflow-hidden truncate text-ellipsis whitespace-nowrap", isHeaderField && "max-w-40")}>{field.value}</span>
                </span>
            }
        />
    );
}

function renderCityContent(field: FieldDataType, isHeaderField: boolean): React.ReactNode {
    let { value } = field;

    if (value && typeof value === "object" && !value.name) {
        // Only unwrap if not already in {name, postal_code} format
        value = value.value ?? value.raw_value;
    }

    if (!value || typeof value !== "object" || !value.name) {
        return null;
    }

    return (
        <span className={cn("flex-1 overflow-hidden truncate text-ellipsis whitespace-nowrap", isHeaderField && "max-w-40")} title={value.name}>
            {value.name}
            {value.postal_code && <span className="text-xs text-gray-500"> ({value.postal_code})</span>}
        </span>
    );
}

function renderBooleanContent(field: FieldDataType, isHeaderField: boolean): React.ReactNode {
    let { value } = field;

    if (value && typeof value === "object") {
        value = value.value ?? value.raw_value;
    }

    return (
        <span className={cn("flex-1 overflow-hidden truncate text-ellipsis whitespace-nowrap", isHeaderField && "max-w-40")} title={field.value ? "Yes" : "No"}>
            {value ? "Yes" : "No"}
        </span>
    );
}

function renderAgeContent(field: FieldDataType, isHeaderField: boolean): React.ReactNode {
    let { value } = field;

    if (value && typeof value === "object") {
        value = value.value ?? value.raw_value;
    }

    return (
        <span className={cn("flex-1 overflow-hidden truncate text-ellipsis whitespace-nowrap", isHeaderField && "max-w-40")} title={field.value}>
            {value} yo.
        </span>
    );
}

function renderSinceContent(field: FieldDataType, isHeaderField: boolean): React.ReactNode {
    let { value } = field;

    if (value && typeof value === "object") {
        value = value.value ?? value.raw_value;
    }

    return (
        <span className={cn("flex-1 overflow-hidden truncate text-ellipsis whitespace-nowrap", isHeaderField && "max-w-40")} title={formatDateValue(value, "long")}>
            {timeAgo(value)}
        </span>
    );
}

function renderRegistrationStatusContent(field: FieldDataType, _isHeaderField: boolean): React.ReactNode {
    let { value } = field;

    if (value && typeof value === "object") {
        value = value.value ?? value.raw_value;
    }

    return get_apprenticeship_status_badge(value as ApprenticeshipRegistrationStatus);
}

function renderBadgeContent(field: FieldDataType): React.ReactNode {
    const raw = field.value;
    if (!raw) return null;

    // Accepts both a plain string and the structured object returned by the backend.
    const label: string = typeof raw === "string" ? raw : ((raw as any).label ?? (raw as any).value ?? String(raw));
    const variant: string = typeof raw === "object" ? ((raw as any).variant ?? "default") : "default";
    // `tone` is the design system value (`crm.object_config.badge`); `variant` is the legacy one.
    const tone: BadgeTone | undefined = typeof raw === "object" ? (raw as any).tone : undefined;
    const tooltip: string | undefined = typeof raw === "object" ? (raw as any).tooltip : undefined;

    return (
        <Badge variant={variant as any} tone={tone} title={tooltip}>
            {label}
        </Badge>
    );
}

function renderApprenticeshipTypeContent(field: FieldDataType, _isHeaderField: boolean): React.ReactNode {
    return (
        <span className="flex flex-nowrap items-center gap-1">
            [{field.value.type === "Apprentissage" ? "App" : "CP"}] {field.value.product}
        </span>
    );
}

function renderDateTimeContent(field: FieldDataType, isHeaderField: boolean): React.ReactNode {
    let { value } = field;

    if (value && typeof value === "object") {
        value = value.value ?? value.raw_value;
    }

    const formattedValue = formatDateValue(new Date(value), "long");

    return (
        <span className={cn("flex-1 overflow-hidden truncate text-ellipsis whitespace-nowrap", isHeaderField && "max-w-40")} title={formattedValue}>
            {formattedValue}
        </span>
    );
}

function renderDateContent(field: FieldDataType, isHeaderField: boolean): React.ReactNode {
    let { value } = field;

    if (value && typeof value === "object") {
        value = value.value ?? value.raw_value;
    }

    const formattedValue = formatDateValue(value.split("T")[0].split(" ")[0], "long");

    return (
        <span className={cn("flex-1 overflow-hidden truncate text-ellipsis whitespace-nowrap", isHeaderField && "max-w-40")} title={formattedValue}>
            {formattedValue}
        </span>
    );
}

function renderTimeContent(field: FieldDataType, isHeaderField: boolean): React.ReactNode {
    let { value } = field;

    if (value && typeof value === "object") {
        value = value.value ?? value.raw_value;
    }

    const formattedValue = formatHumanDuration(value);

    return (
        <span className={cn("flex-1 overflow-hidden truncate text-ellipsis whitespace-nowrap", isHeaderField && "max-w-40")} title={formattedValue}>
            {formattedValue}
        </span>
    );
}

/** Renders an integer number of seconds as HH:MM:SS (e.g. 3661 → "01:01:01"). */
function renderDurationContent(field: FieldDataType, isHeaderField: boolean): React.ReactNode {
    let { value } = field;

    if (value && typeof value === "object") {
        value = value.value ?? value.raw_value;
    }

    const formattedValue = formatDurationHHMMSS(Number(value));

    return (
        <span className={cn("flex-1 overflow-hidden truncate text-ellipsis whitespace-nowrap", isHeaderField && "max-w-40")} title={formattedValue}>
            {formattedValue}
        </span>
    );
}

/** Renders a ratio (0-1) as percentage string (e.g. 0.219 -> "21,9 %"). */
function renderPercentageContent(field: FieldDataType, isHeaderField: boolean): React.ReactNode {
    let { value } = field;

    if (value && typeof value === "object") {
        value = value.value ?? value.raw_value;
    }

    const formattedValue = formatPercentageFromRatio(Number(value));

    return (
        <span className={cn("flex-1 overflow-hidden truncate text-ellipsis whitespace-nowrap", isHeaderField && "max-w-40")} title={formattedValue}>
            {formattedValue}
        </span>
    );
}

function renderCurrencyContent(field: FieldDataType, isHeaderField: boolean): React.ReactNode {
    let { value } = field;

    if (value && typeof value === "object") {
        value = value.value ?? value.raw_value;
    }

    const formattedValue = formatCurrencyValue(value);

    return (
        <span className={cn("flex-1 overflow-hidden truncate text-ellipsis whitespace-nowrap", isHeaderField && "max-w-40")} title={formattedValue}>
            {formattedValue}
        </span>
    );
}

function renderUrlContent(field: FieldDataType, isHeaderField: boolean): React.ReactNode {
    let { value, href, href_label } = field;

    if (value && typeof value === "object") {
        value = value.value ?? value.raw_value;
    }

    return (
        <Link
            to={href || value}
            target={href?.startsWith("http") || value?.startsWith("http") ? "_blank" : undefined}
            className={cn("min-w-0 flex-1 truncate text-blue-600 hover:text-blue-800 hover:underline", isHeaderField && "max-w-40")}
            title={href_label || value}
        >
            {href_label || value}
        </Link>
    );
}

function renderDefaultContent(
    field: FieldDataType,
    options: { showUnit: boolean; largeField: boolean; valueClassName?: string; layout: "grid" | "flex"; isHeaderField?: boolean; truncate?: boolean }
): React.ReactNode {
    let {
        // label,
        value,
        field_type,
        href,
        href_label,
        unit
    } = field;
    const { showUnit, largeField, valueClassName, layout, isHeaderField, truncate = true } = options;

    if (value && typeof value === "object") {
        value = value.value ?? value.raw_value;
    }

    const formattedValue = formatFieldValue(value, field);
    let displayValue = formattedValue;

    // Specific formatting for certain types
    if (field_type === "text" && typeof value === "string" && layout === "flex") {
        displayValue = (value.charAt(0).toUpperCase() + value.replace(/_/g, " ").slice(1)).replace(/_/g, " ");
    } else if (typeof value === "number") {
        if (unit == "%") {
            displayValue = (value * 100).toLocaleString("fr-FR");
        } else {
            displayValue = value.toLocaleString("fr-FR");
        }
    }

    if (href) {
        const titleValue = href_label || (typeof displayValue === "string" ? displayValue : String(value));
        return (
            <Link
                to={href}
                target={href.startsWith("http") ? "_blank" : undefined}
                className={cn("min-w-0 flex-1", truncate && "truncate", "text-blue-600 hover:text-blue-800 hover:underline", isHeaderField && "max-w-40")}
                title={titleValue}
            >
                {href_label || displayValue}
            </Link>
        );
    }

    return (
        <span className={cn("min-w-0 flex-1", truncate && "truncate", valueClassName, largeField && "whitespace-pre-wrap", isHeaderField && "max-w-40")} title={value}>
            {displayValue}
            {showUnit && unit && typeof displayValue === "string" && ` ${unit}`}
        </span>
    );
}

/** Renders a JSON value (object or array) indented, as stored. */
function renderJsonContent(field: FieldDataType): React.ReactNode {
    const { value } = field;
    if (value == null) return <span className="text-sm">-</span>;
    const isEmptyObject = typeof value === "object" && Object.keys(value).length === 0;
    if (isEmptyObject) return <span className="text-sm">-</span>;

    const text = typeof value === "string" ? value : JSON.stringify(value, null, 2);
    return <pre className="min-w-0 flex-1 overflow-x-auto whitespace-pre-wrap break-words font-mono text-xs text-gray-800">{text}</pre>;
}

function renderMarkdownContent(
    field: FieldDataType,
    options: { showUnit: boolean; largeField: boolean; valueClassName?: string; layout: "grid" | "flex"; isHeaderField?: boolean; truncate?: boolean }
): React.ReactNode {
    let { value } = field;

    if (value && typeof value === "object") {
        value = value.value ?? value.raw_value;
    }

    const { valueClassName, isHeaderField, truncate = true } = options;
    return (
        <span className={cn("min-w-0 flex-1", truncate && "truncate", valueClassName, isHeaderField && "max-w-40")} title={value}>
            <SimpleMarkdownRenderer content={value} />
        </span>
    );
}

function renderAlert(
    field: FieldDataType,
    options: { showUnit: boolean; largeField: boolean; valueClassName?: string; layout: "grid" | "flex"; isHeaderField?: boolean; truncate?: boolean }
): React.ReactNode {
    let { value } = field;

    if (value && typeof value === "object") {
        value = value.value ?? value.raw_value;
    }

    const { valueClassName, isHeaderField, truncate = true } = options;
    return (
        <span
            className={cn(
                "min-w-0 flex-1",
                truncate && "truncate",
                "rounded-md px-2",
                valueClassName,
                isHeaderField ? "max-w-40 px-2 py-1" : "p-2",
                field.field_type == "alert-error"
                    ? "bg-red-100 text-red-800"
                    : field.field_type == "alert-warning"
                      ? "bg-amber-100 text-amber-800"
                      : field.field_type == "alert-info"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-gray-100 text-gray-700"
            )}
            title={value}
        >
            <SimpleMarkdownRenderer content={value} className="[&>*:last-child]:mb-0" />
        </span>
    );
}

function renderEmptyField(
    label: string,
    showLabel: boolean,
    labelClassName: string,
    valueClassName: string,
    layout: "grid" | "flex",
    containerClassName: string,
    truncate: boolean = true,
    description?: string | null,
    descriptionTrigger?: FieldDescriptionTrigger
): React.ReactNode {
    const content = <span className={cn(truncate && "truncate text-ellipsis", valueClassName)}>-</span>;

    return renderLayout(content, { label, description, showLabel, labelClassName, layout, containerClassName, truncate, descriptionTrigger });
}
